import { useMemo } from 'react';
import { useBoolean } from 'usehooks-ts';

import { DocumentActionItem } from '@/components/Form/ActionList/DocumentActionItem';

import { MINOR_IDENTITY_DOCUMENTS } from '@/constants/user';

import { useDrawerOpenTracker } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import {
  selectBasicInfoStatus,
  selectOperationUser,
} from '@/redux/operation/selectors';

import { DocumentStatus } from '@/types/document';

import { MinorBasicInfoDrawer } from '../drawers/MinorBasicInfoDrawer';
import { isMinorBasicInfoFullyComplete } from '../utils';

export function MinorBasicInfo() {
  const { t } = useTranslate();
  const minorUser = useAppSelector(selectOperationUser);
  const basicInfoStatus = useAppSelector(selectBasicInfoStatus);

  const {
    value: isDrawerOpen,
    setTrue: openDrawer,
    setFalse: closeDrawer,
  } = useBoolean(false);

  useDrawerOpenTracker(isDrawerOpen);

  const isMissingIdentityDoc = useMemo(() => {
    const documents = minorUser?.documents || [];
    const missingDocuments = MINOR_IDENTITY_DOCUMENTS.filter(
      doc => !documents.some(d => d.type === doc),
    );

    return missingDocuments.length !== 0;
  }, [minorUser]);

  const isBasicInfoFullyComplete = isMinorBasicInfoFullyComplete(
    basicInfoStatus,
    minorUser,
  );

  const isBasicInfoDisabled = useMemo(() => {
    if (basicInfoStatus === DocumentStatus.Rejected) {
      return true;
    }

    // Disable the basic info if the identity check is not completed
    return isMissingIdentityDoc && !minorUser?.adultConfirmKyc;
  }, [basicInfoStatus, isMissingIdentityDoc, minorUser]);

  const basicInfoDisplayStatus = useMemo(
    () =>
      isBasicInfoFullyComplete ? DocumentStatus.Approved : basicInfoStatus,
    [basicInfoStatus, isBasicInfoFullyComplete],
  );

  if (!minorUser) return null;

  return (
    <>
      <DocumentActionItem
        title={t('moreInfo.form.basicInfo.title')}
        description={t('moreInfo.form.basicInfo.description')}
        onClick={openDrawer}
        status={basicInfoDisplayStatus}
        disabled={isBasicInfoDisabled}
      />
      <MinorBasicInfoDrawer
        open={isDrawerOpen}
        onClose={closeDrawer}
        user={minorUser}
      />
    </>
  );
}
